import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X, Package, Inbox, Settings2, Images, Upload, Loader2, DatabaseBackup, Download, RefreshCw } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const EMPTY_PRODUCT = {
  name: "", brand: "", category: "industrial-automation", ptype: "", model: "",
  short_desc: "", specs: "", applications: "", mrp: "", availability: "", datasheet: "", image: "", featured: false,
};

const inputCls =
  "min-h-[42px] w-full border border-[#D9DDE3] bg-white px-3.5 py-2.5 text-sm text-[#161616] focus:border-[#F5B700] focus:outline-none";

function ImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      <div className="flex gap-2">
        <input className={inputCls} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Image URL or upload →" data-testid="image-url-input" />
        <label className="flex min-h-[42px] shrink-0 cursor-pointer items-center gap-2 bg-[#111111] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#F5B700] hover:text-[#111111]" data-testid="image-upload-btn">
          {uploading ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}
          Upload
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      {value && (
        <img src={value.startsWith("/api/") ? `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL)}${value}` : value} alt="Preview" className="mt-2 h-20 w-32 border border-[#D9DDE3] object-cover" data-testid="image-preview" />
      )}
    </div>
  );
}

function EnquiriesTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/enquiries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.patch(`/enquiries/${id}`, { status: "read" });
    load();
  };

  if (items.length === 0)
    return <p className="border border-[#D9DDE3] bg-white p-10 text-center text-sm text-[#667085]" data-testid="enquiries-empty">No enquiries yet. New form submissions will appear here.</p>;

  return (
    <div className="space-y-3" data-testid="enquiries-list">
      {items.map((e) => (
        <div key={e.id} className={`border bg-white p-5 ${e.status === "new" ? "border-[#F5B700]" : "border-[#D9DDE3]"}`} data-testid={`enquiry-${e.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-heading text-base font-semibold text-[#161616]">
                {e.name} {e.company ? <span className="text-[#667085]">· {e.company}</span> : ""}
              </p>
              <p className="mt-0.5 text-xs text-[#667085]">
                {e.phone}{e.email ? ` · ${e.email}` : ""} · {new Date(e.created_at).toLocaleString("en-IN")}
              </p>
            </div>
            {e.status === "new" ? (
              <button onClick={() => markRead(e.id)} data-testid={`enquiry-read-${e.id}`} className="min-h-[36px] border border-[#111111] px-3 py-1.5 text-xs font-semibold hover:bg-[#111111] hover:text-white">
                Mark read
              </button>
            ) : (
              <span className="text-xs font-semibold uppercase tracking-wide text-[#18864B]">Read</span>
            )}
          </div>
          {(e.product_name || e.model || e.quantity) && (
            <p className="mt-3 text-sm text-[#161616]">
              <strong>Product:</strong> {e.product_name || "—"} {e.model ? `· Model: ${e.model}` : ""} {e.quantity ? `· Qty: ${e.quantity}` : ""}
            </p>
          )}
          <p className="mt-2 text-sm leading-relaxed text-[#667085]">{e.requirement}</p>
        </div>
      ))}
    </div>
  );
}

function ProductsTab() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [brandF, setBrandF] = useState("");
  const [catF, setCatF] = useState("");

  const load = () => {
    api.get("/products").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/products/meta").then((r) => setMeta(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = items.filter((p) => {
    if (brandF && p.brand !== brandF) return false;
    if (catF && p.category !== catF) return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    const hay = [p.name, p.brand, p.model, p.ptype, p.category, ...(Array.isArray(p.specs) ? p.specs : [])].join(" ").toLowerCase();
    return q.split(/\s+/).every((w) => hay.includes(w));
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...editing,
      specs: typeof editing.specs === "string" ? editing.specs.split("\n").filter(Boolean) : editing.specs,
      applications: typeof editing.applications === "string" ? editing.applications.split("\n").filter(Boolean) : editing.applications,
      mrp: editing.mrp === "" || editing.mrp == null ? null : Number(editing.mrp),
    };
    delete payload.id;
    delete payload.created_at;
    try {
      if (editing.id) await api.put(`/products/${editing.id}`, payload);
      else await api.post("/products", payload);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const startEdit = (p) =>
    setEditing({
      ...EMPTY_PRODUCT, ...p,
      specs: Array.isArray(p.specs) ? p.specs.join("\n") : p.specs || "",
      applications: Array.isArray(p.applications) ? p.applications.join("\n") : p.applications || "",
      mrp: p.mrp ?? "",
    });

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#667085]" data-testid="products-count">{filtered.length} of {items.length} products</p>
        <button onClick={() => setEditing({ ...EMPTY_PRODUCT })} data-testid="add-product-btn" className="flex min-h-[42px] items-center gap-2 bg-[#F5B700] px-4 py-2.5 text-sm font-semibold text-[#111111] hover:bg-[#D99C00]">
          <Plus size={15} aria-hidden="true" /> Add Product
        </button>
      </div>

      <div className="mb-4 flex flex-col gap-2 border border-[#D9DDE3] bg-white p-3 sm:flex-row" data-testid="admin-product-search">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, model no, size, brand…"
          data-testid="admin-search-input"
          className={`${inputCls} sm:flex-1`}
        />
        <select value={brandF} onChange={(e) => setBrandF(e.target.value)} data-testid="admin-brand-filter" className={`${inputCls} sm:w-48`}>
          <option value="">All Brands</option>
          {(meta?.brands || []).map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={catF} onChange={(e) => setCatF(e.target.value)} data-testid="admin-category-filter" className={`${inputCls} sm:w-56`}>
          <option value="">All Categories</option>
          {(meta?.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        {(query || brandF || catF) && (
          <button onClick={() => { setQuery(""); setBrandF(""); setCatF(""); }} data-testid="admin-search-clear" className="min-h-[42px] border border-[#D9DDE3] px-3 text-xs font-semibold hover:border-[#111111]">
            Clear
          </button>
        )}
      </div>

      <div className="overflow-x-auto border border-[#D9DDE3] bg-white" data-testid="products-table">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-[#D9DDE3] bg-[#F6F7F8] text-xs uppercase tracking-wide text-[#667085]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">MRP</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-[#667085]" data-testid="admin-search-empty">No products match your search.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-[#eef0f3] last:border-0" data-testid={`admin-product-${p.id}`}>
                <td className="px-4 py-3 font-medium text-[#161616]">{p.name}</td>
                <td className="px-4 py-3 text-[#667085]">{p.brand}</td>
                <td className="px-4 py-3 text-[#667085]">{p.category}</td>
                <td className="px-4 py-3 text-[#667085]">{p.mrp != null ? `₹${p.mrp}` : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => startEdit(p)} aria-label={`Edit ${p.name}`} data-testid={`edit-product-${p.id}`} className="mr-1 inline-flex h-9 w-9 items-center justify-center border border-[#D9DDE3] hover:border-[#111111]">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} aria-label={`Delete ${p.name}`} data-testid={`delete-product-${p.id}`} className="inline-flex h-9 w-9 items-center justify-center border border-[#D9DDE3] text-[#C62828] hover:border-[#C62828]">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" role="dialog" aria-modal="true" data-testid="product-editor">
          <form onSubmit={save} className="mx-auto my-8 max-w-2xl bg-white p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold">{editing.id ? "Edit Product" : "Add Product"}</h2>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close editor" data-testid="editor-close" className="flex h-9 w-9 items-center justify-center border border-[#D9DDE3]">
                <X size={16} />
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-name">Product Name *</label>
                <input id="ep-name" data-testid="ep-name" required className={inputCls} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-brand">Brand</label>
                <input id="ep-brand" data-testid="ep-brand" className={inputCls} value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-category">Category</label>
                <select id="ep-category" data-testid="ep-category" className={inputCls} value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {(meta?.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-type">Product Type</label>
                <input id="ep-type" data-testid="ep-type" className={inputCls} value={editing.ptype} onChange={(e) => setEditing({ ...editing, ptype: e.target.value })} placeholder="e.g. Temperature Controller" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-model">Model Number</label>
                <input id="ep-model" data-testid="ep-model" className={inputCls} value={editing.model} onChange={(e) => setEditing({ ...editing, model: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-mrp">MRP (₹) — leave blank to show "Contact for Latest Price"</label>
                <input id="ep-mrp" data-testid="ep-mrp" type="number" min="0" step="0.01" className={inputCls} value={editing.mrp} onChange={(e) => setEditing({ ...editing, mrp: e.target.value })} />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-availability">Availability</label>
                <input id="ep-availability" data-testid="ep-availability" className={inputCls} value={editing.availability} onChange={(e) => setEditing({ ...editing, availability: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-image">Image (paste URL or upload a photo)</label>
                <ImageField value={editing.image} onChange={(url) => setEditing({ ...editing, image: url })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-desc">Short Description</label>
                <textarea id="ep-desc" data-testid="ep-desc" rows={2} className={inputCls} value={editing.short_desc} onChange={(e) => setEditing({ ...editing, short_desc: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-specs">Specifications (one per line)</label>
                <textarea id="ep-specs" data-testid="ep-specs" rows={3} className={inputCls} value={editing.specs} onChange={(e) => setEditing({ ...editing, specs: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-apps">Applications (one per line)</label>
                <textarea id="ep-apps" data-testid="ep-apps" rows={2} className={inputCls} value={editing.applications} onChange={(e) => setEditing({ ...editing, applications: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-semibold" htmlFor="ep-datasheet">Datasheet URL</label>
                <input id="ep-datasheet" data-testid="ep-datasheet" className={inputCls} value={editing.datasheet} onChange={(e) => setEditing({ ...editing, datasheet: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm font-medium sm:col-span-2">
                <input type="checkbox" data-testid="ep-featured" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="h-4 w-4 accent-[#F5B700]" />
                Show in homepage spotlight
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button type="submit" disabled={saving} data-testid="ep-save" className="min-h-[44px] bg-[#111111] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#F5B700] hover:text-[#111111] disabled:opacity-60">
                {saving ? "Saving…" : "Save Product"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="min-h-[44px] border border-[#D9DDE3] px-6 py-2.5 text-sm font-semibold">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!form) return <p className="text-sm text-[#667085]">Loading settings…</p>;

  const fields = [
    ["business_name", "Business Name"], ["tagline", "Tagline"], ["phone", "Phone (display)"],
    ["phone_raw", "Phone (tel: format, e.g. +918279221172)"], ["secondary_phone", "Secondary Phone"],
    ["email", "Email"], ["whatsapp", "WhatsApp Number (digits only)"],
    ["address_line1", "Address Line 1"], ["address_line2", "Address Line 2"],
    ["hours_weekday", "Weekday Hours"], ["hours_sunday", "Sunday Hours"],
    ["maps_url", "Google Maps Link"], ["maps_embed", "Google Maps Embed URL"],
    ["google_rating", "Google Rating"], ["google_reviews_count", "Google Reviews Count"],
    ["google_reviews_url", "Google Reviews URL"], ["social_instagram", "Instagram URL"], ["established", "Established Year"],
    ["location", "Location"], ["gstin", "GSTIN"], ["seo_title", "SEO Title"], ["seo_description", "SEO Meta Description"],
  ];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Business information saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="border border-[#D9DDE3] bg-white p-6 sm:p-8" data-testid="settings-form">
      <div className="mb-6 border border-[#F5B700] bg-[#FFFBEA] p-4" data-testid="festive-banner-settings">
        <h3 className="font-heading text-base font-bold text-[#161616]">Festive / Seasonal Banner</h3>
        <p className="mt-1 text-xs text-[#667085]">Shows a slim offer strip at the top of the homepage. Switch it on for Diwali or any promotion, and off when it ends.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold" htmlFor="st-festive_banner_enabled">Banner Status</label>
            <select id="st-festive_banner_enabled" data-testid="st-festive_banner_enabled" className={inputCls} value={form.festive_banner_enabled ?? "false"} onChange={(e) => setForm({ ...form, festive_banner_enabled: e.target.value })}>
              <option value="false">Off (hidden)</option>
              <option value="true">On (visible on homepage)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold" htmlFor="st-festive_banner_text">Banner Text</label>
            <input id="st-festive_banner_text" data-testid="st-festive_banner_text" className={inputCls} value={form.festive_banner_text ?? ""} onChange={(e) => setForm({ ...form, festive_banner_text: e.target.value })} placeholder="e.g. Diwali offers now available — contact us for details" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold" htmlFor="st-festive_banner_cta">Button Label (optional)</label>
            <input id="st-festive_banner_cta" data-testid="st-festive_banner_cta" className={inputCls} value={form.festive_banner_cta ?? ""} onChange={(e) => setForm({ ...form, festive_banner_cta: e.target.value })} placeholder="e.g. Enquire Now" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold" htmlFor="st-festive_banner_link">Button Links To (optional)</label>
            <input id="st-festive_banner_link" data-testid="st-festive_banner_link" className={inputCls} value={form.festive_banner_link ?? ""} onChange={(e) => setForm({ ...form, festive_banner_link: e.target.value })} placeholder="/quote" />
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.map(([key, label]) => (
          <div key={key} className={key.includes("seo") || key.includes("maps") ? "sm:col-span-2" : ""}>
            <label className="mb-1 block text-xs font-semibold" htmlFor={`st-${key}`}>{label}</label>
            <input id={`st-${key}`} data-testid={`st-${key}`} className={inputCls} value={form[key] ?? ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
          </div>
        ))}
      </div>
      <button type="submit" disabled={saving} data-testid="settings-save" className="mt-6 min-h-[44px] bg-[#111111] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#F5B700] hover:text-[#111111] disabled:opacity-60">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </form>
  );
}

const GALLERY_CATS = ["Store", "Products", "Electrical", "Automation", "Switchgear", "Pneumatic", "Team"];

function GalleryTab() {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/gallery").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!adding.image) { toast.error("Please upload or paste an image first"); return; }
    setSaving(true);
    try {
      if (adding.id) await api.put(`/gallery/${adding.id}`, adding);
      else await api.post("/gallery", adding);
      toast.success("Gallery item saved");
      setAdding(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this gallery image?")) return;
    await api.delete(`/gallery/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-[#667085]">{items.length} gallery images. Upload real store and product photos here.</p>
        <button onClick={() => setAdding({ category: "Store", image: "", caption: "" })} data-testid="add-gallery-btn" className="flex min-h-[42px] items-center gap-2 bg-[#F5B700] px-4 py-2.5 text-sm font-semibold text-[#111111] hover:bg-[#D99C00]">
          <Plus size={15} aria-hidden="true" /> Add Photo
        </button>
      </div>

      {adding && (
        <div className="mb-6 border border-[#F5B700] bg-white p-6" data-testid="gallery-editor">
          <h3 className="font-heading text-lg font-semibold">{adding.id ? "Edit Photo" : "Add Photo"}</h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold">Category</label>
              <select className={inputCls} value={adding.category} onChange={(e) => setAdding({ ...adding, category: e.target.value })} data-testid="gallery-category">
                {GALLERY_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold">Caption</label>
              <input className={inputCls} value={adding.caption} onChange={(e) => setAdding({ ...adding, caption: e.target.value })} data-testid="gallery-caption" placeholder="e.g. Our store in Tahliwal" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-semibold">Photo</label>
              <ImageField value={adding.image} onChange={(url) => setAdding({ ...adding, image: url })} />
            </div>
          </div>
          <div className="mt-5 flex gap-3">
            <button onClick={save} disabled={saving} data-testid="gallery-save" className="min-h-[42px] bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#F5B700] hover:text-[#111111] disabled:opacity-60">
              {saving ? "Saving…" : "Save Photo"}
            </button>
            <button onClick={() => setAdding(null)} className="min-h-[42px] border border-[#D9DDE3] px-5 py-2.5 text-sm font-semibold">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-admin-grid">
        {items.map((g) => (
          <div key={g.id} className="border border-[#D9DDE3] bg-white" data-testid={`gallery-admin-${g.id}`}>
            <div className="aspect-[4/3] bg-[#e5e8ec]">
              {g.image && (
                <img
                  src={g.image.startsWith("/api/") ? `${(window.BACKEND_URL || process.env.REACT_APP_BACKEND_URL)}${g.image}` : g.image}
                  alt={g.caption || g.category}
                  className="h-full w-full object-cover"
                />
              )}
            </div>
            <div className="flex items-center justify-between gap-2 p-3">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#8a6800]">{g.category}</p>
                <p className="truncate text-xs text-[#667085]">{g.caption}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button onClick={() => setAdding({ ...g })} aria-label="Edit" data-testid={`gallery-edit-${g.id}`} className="flex h-9 w-9 items-center justify-center border border-[#D9DDE3] hover:border-[#111111]">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(g.id)} aria-label="Delete" data-testid={`gallery-delete-${g.id}`} className="flex h-9 w-9 items-center justify-center border border-[#D9DDE3] text-[#C62828] hover:border-[#C62828]">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BackupTab() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [sourceUrl, setSourceUrl] = useState("");
  const [repair, setRepair] = useState(null);
  const [repairing, setRepairing] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/catalogue/export", { responseType: "blob", timeout: 60000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "hariom-catalogue-backup.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRestoring(true);
    setRestoreResult(null);
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/admin/catalogue/import", payload, { timeout: 120000 });
      setRestoreResult(data.imported);
      toast.success("Catalogue restored");
    } catch (err) {
      toast.error(err instanceof SyntaxError ? "That file is not a valid backup JSON" : formatApiError(err));
    } finally {
      setRestoring(false);
      e.target.value = "";
    }
  };

  const startRepair = async () => {
    setRepairing(true);
    try {
      await api.post("/admin/catalogue/repair-files", { source_url: sourceUrl });
      pollRepair();
    } catch (e) {
      toast.error(formatApiError(e));
      setRepairing(false);
    }
  };

  const pollRepair = async () => {
    try {
      const { data } = await api.get("/admin/catalogue/repair-status");
      setRepair(data);
      if (data.running) setTimeout(pollRepair, 3000);
      else { setRepairing(false); if (data.done) toast.success(`Image repair finished — ${data.copied} copied, ${data.failed} failed`); }
    } catch {
      setRepairing(false);
    }
  };

  const cardCls = "border border-[#D9DDE3] bg-white p-5";
  const btnCls = "flex min-h-[42px] items-center gap-2 bg-[#111111] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#F5B700] hover:text-[#111111] disabled:opacity-50";

  return (
    <div className="space-y-5" data-testid="backup-tab">
      <div className={cardCls}>
        <h3 className="font-heading text-base font-bold text-[#161616]">Download Catalogue Backup</h3>
        <p className="mt-1 text-sm text-[#667085]">Downloads all products, business info, FAQs, reviews, gallery and file records as one JSON file.</p>
        <button onClick={download} disabled={downloading} className={`${btnCls} mt-4`} data-testid="backup-download-btn">
          {downloading ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Download size={15} aria-hidden="true" />}
          {downloading ? "Preparing…" : "Download Backup"}
        </button>
      </div>

      <div className={cardCls}>
        <h3 className="font-heading text-base font-bold text-[#161616]">Restore Catalogue Backup</h3>
        <p className="mt-1 text-sm text-[#667085]">Upload a backup JSON to restore the full catalogue on this site. Existing entries with the same IDs are updated, nothing is deleted.</p>
        <label className={`${btnCls} mt-4 inline-flex cursor-pointer`} data-testid="backup-restore-btn">
          {restoring ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Upload size={15} aria-hidden="true" />}
          {restoring ? "Restoring…" : "Choose Backup File"}
          <input type="file" accept="application/json,.json" className="hidden" onChange={restore} disabled={restoring} data-testid="backup-restore-input" />
        </label>
        {restoreResult && (
          <p className="mt-3 text-sm text-[#067647]" data-testid="backup-restore-result">
            Restored: {Object.entries(restoreResult).map(([k, v]) => `${v} ${k}`).join(", ")}
          </p>
        )}
      </div>

      <div className={cardCls}>
        <h3 className="font-heading text-base font-bold text-[#161616]">Repair Images & Datasheets</h3>
        <p className="mt-1 text-sm text-[#667085]">If product images or PDF datasheets are missing on this site after a restore, enter the address of the site that has them (e.g. the preview site) and they will be copied across automatically.</p>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <input className={inputCls} value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder="https://your-preview-site.preview.emergentagent.com" data-testid="repair-source-input" />
          <button onClick={startRepair} disabled={repairing || !sourceUrl.trim()} className={`${btnCls} shrink-0`} data-testid="repair-start-btn">
            {repairing ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={15} aria-hidden="true" />}
            {repairing ? "Repairing…" : "Start Repair"}
          </button>
        </div>
        {repair && (
          <p className="mt-3 text-sm text-[#667085]" data-testid="repair-status">
            Checked {repair.checked}/{repair.total} files — {repair.copied} copied, {repair.failed} failed{repair.running ? " (still running…)" : ""}
          </p>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { id: "enquiries", label: "Enquiries", icon: Inbox },
  { id: "products", label: "Products", icon: Package },
  { id: "gallery", label: "Gallery", icon: Images },
  { id: "settings", label: "Business Info", icon: Settings2 },
  { id: "backup", label: "Backup", icon: DatabaseBackup },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("enquiries");
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("hotc_admin_token")) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("hotc_admin_token");
        navigate("/admin");
      });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("hotc_admin_token");
    api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-[#F6F7F8] text-sm text-[#667085]">Checking session…</div>;

  return (
    <div className="min-h-screen bg-[#F6F7F8]" data-testid="admin-dashboard">
      <header className="border-b border-[#2c2c2e] bg-[#111111]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6">
          <Link to="/"><Logo dark compact /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-[#98a2b3] sm:block" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex min-h-[40px] items-center gap-2 border border-[#3a3d42] px-4 py-2 text-sm font-semibold text-white hover:border-[#F5B700] hover:text-[#F5B700]">
              <LogOut size={15} aria-hidden="true" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h1 className="font-heading text-3xl font-bold text-[#161616]">Dashboard</h1>
        <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar" role="tablist" aria-label="Admin sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex min-h-[44px] shrink-0 items-center gap-2 border px-5 py-2.5 text-sm font-semibold ${
                tab === t.id ? "border-[#111111] bg-[#111111] text-[#F5B700]" : "border-[#D9DDE3] bg-white text-[#161616]"
              }`}
            >
              <t.icon size={15} aria-hidden="true" /> {t.label}
            </button>
          ))}
        </div>
        <div className="mt-6">
          {tab === "enquiries" && <EnquiriesTab />}
          {tab === "products" && <ProductsTab />}
          {tab === "gallery" && <GalleryTab />}
          {tab === "settings" && <SettingsTab />}
          {tab === "backup" && <BackupTab />}
        </div>
      </div>
    </div>
  );
}
